import { useEffect } from "react";

const EMBED_SRC = "https://www.instagram.com/embed.js";

declare global {
  interface Window {
    instgrm?: { Embeds?: { process?: () => void } };
  }
}

let embedScriptPromise: Promise<void> | null = null;

function loadInstagramEmbedScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.instgrm?.Embeds?.process) return Promise.resolve();
  if (embedScriptPromise) return embedScriptPromise;

  embedScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${EMBED_SRC}"]`);
    if (existing) {
      if (window.instgrm?.Embeds?.process) {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener(
          "error",
          () => reject(new Error("Failed to load Instagram embed script")),
          { once: true },
        );
      }
      return;
    }
    const s = document.createElement("script");
    s.src = EMBED_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      embedScriptPromise = null;
      reject(new Error("Failed to load Instagram embed script"));
    };
    document.body.appendChild(s);
  });

  return embedScriptPromise;
}

export function InstagramEmbed({ url, title }: { url: string; title: string }) {
  useEffect(() => {
    let cancelled = false;
    loadInstagramEmbedScript()
      .then(() => {
        if (cancelled) return;
        window.instgrm?.Embeds?.process?.();
      })
      .catch(() => {
        /* fallback blockquote stays visible */
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const permalink = `${url}?utm_source=ig_embed&utm_campaign=loading`;

  return (
    <blockquote
      className="instagram-media"
      data-instgrm-captioned
      data-instgrm-permalink={permalink}
      data-instgrm-version="14"
      style={{
        background: "#FFF",
        border: 0,
        borderRadius: 3,
        boxShadow: "0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)",
        margin: "1px auto",
        maxWidth: 540,
        minWidth: 280,
        padding: 0,
        width: "calc(100% - 2px)",
      }}
    >
      <div style={{ padding: 16 }}>
        <a href={permalink} target="_blank" rel="noopener noreferrer">
          {title} — voir cette publication sur Instagram
        </a>
      </div>
    </blockquote>
  );
}

export default InstagramEmbed;
